import { env } from './env';

export type RuntimeConfig = {
  authAuthority: string;
  authClientId: string;
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/**
 * `/config.json` is written by the release (docs/deployment.md). No file (local dev) falls back to the build values;
 * a file without both OIDC values throws, so a broken deploy fails at start instead of signing in against nothing.
 */
export async function getRuntimeConfig(): Promise<RuntimeConfig> {
  const res = await fetch(env.configUrl).catch(() => undefined);
  // The Vite dev server answers a missing file with index.html and a 200, so only a JSON response counts as the file.
  const isJson = res?.headers.get('content-type')?.includes('json') ?? false;

  if (!res?.ok || !isJson) {
    return { authAuthority: env.authAuthority, authClientId: env.authClientId };
  }

  const config: unknown = await res.json().catch(() => undefined);

  if (
    typeof config === 'object' &&
    config !== null &&
    'authAuthority' in config &&
    'authClientId' in config &&
    isNonEmptyString(config.authAuthority) &&
    isNonEmptyString(config.authClientId)
  ) {
    return { authAuthority: config.authAuthority, authClientId: config.authClientId };
  }

  throw new Error(`${env.configUrl} needs non-empty authAuthority and authClientId`);
}
